import { createElement, lazy, type ComponentType } from "react";

/**
 * How wide the page being loaded will be, so the loading box can sit where the
 * page's own content will. `list` (the default) is the whole content width;
 * `form` is `ManagePage`'s 600px column (the shift page). It is a hint for a
 * placeholder, never a layout rule — the page still lays itself out.
 */
export type PageWidthHint = "list" | "form";

type PendingPage = { readonly token: symbol; readonly width: PageWidthHint };

/*
 * "Which page chunk is the route waiting for, if any" — an external store so the
 * shell can read it urgently (`useSyncExternalStore`) while the navigation that
 * caused it is still a pending transition that React has not committed.
 */
let pending: PendingPage | null = null;
const listeners = new Set<() => void>();

function setPending(next: PendingPage | null) {
  pending = next;
  // Never notify from inside a render (`Page` below marks during render).
  queueMicrotask(() => listeners.forEach((listener) => listener()));
}

export const pendingPage = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot: (): PendingPage | null => pending,
  /** The shell committed a route: whatever was being waited for no longer matters. */
  clear() {
    if (pending) setPending(null);
  },
};

/** Every registered page loader, in route order, for prefetching. */
const loaders: Array<() => Promise<unknown>> = [];

/**
 * `React.lazy` for a NAMED export, which is how every page in this app is
 * exported.
 *
 * Three things beyond `lazy`, all there so that opening a page does not feel
 * like a page load:
 *
 * 1. The loader is registered, so `prefetchPages` can fetch every chunk before
 *    anyone clicks.
 * 2. Once the chunk is in, the `lazy` factory answers with a SYNCHRONOUS
 *    thenable. React's lazy initializer only suspends when the thenable has not
 *    already called back, so a prefetched page renders in the same pass as the
 *    navigation — no suspended render, no fallback, not even for a frame. (A
 *    native promise, even an already-resolved one, always suspends once.)
 * 3. While the chunk is genuinely missing, the page reports itself to
 *    `pendingPage`, which is what the shell uses to decide that the box is due.
 */
// `any` props: each page keeps its own prop type through `C`; the constraint
// only has to be what `React.lazy` itself asks for.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyNamed<K extends string, C extends ComponentType<any>>(
  load: () => Promise<Record<K, C>>,
  name: K,
  options: { width?: PageWidthHint } = {},
) {
  const token = Symbol(name);
  const width = options.width ?? "list";
  let loaded: C | null = null;
  let inflight: Promise<C> | null = null;

  const fetchPage = (): Promise<C> => {
    inflight ??= load().then(
      (module) => {
        loaded = module[name];
        if (pending?.token === token) setPending(null);
        return loaded;
      },
      (failure: unknown) => {
        // A failed fetch must not be remembered: the next visit retries.
        inflight = null;
        if (pending?.token === token) setPending(null);
        throw failure;
      },
    );
    return inflight;
  };
  loaders.push(fetchPage);

  const Lazy = lazy(() => {
    if (loaded) {
      const component = loaded;
      const ready = {
        then(onFulfilled?: (value: { default: C }) => unknown) {
          onFulfilled?.({ default: component });
        },
      };
      return ready as unknown as Promise<{ default: C }>;
    }
    return fetchPage().then((component) => ({ default: component }));
  });

  function Page(props: object) {
    if (!loaded && pending?.token !== token) setPending({ token, width });
    return createElement(Lazy as unknown as ComponentType<object>, props);
  }
  Page.displayName = `Page(${name})`;
  return Page as unknown as typeof Lazy;
}

const BATCH = 4;
let requestedCount = 0;

type IdleWindow = Window & {
  requestIdleCallback?: (
    cb: () => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/** A metered or slow connection should not pay for pages nobody asked for. */
function savingData(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  return Boolean(
    connection?.saveData ||
    connection?.effectiveType === "slow-2g" ||
    connection?.effectiveType === "2g",
  );
}

/**
 * Fetch every page chunk, a few at a time, when the browser is idle. Called
 * once the shell is on screen, so the first click on any sidebar row finds its
 * page already in memory. Failures are swallowed: a prefetch that misses just
 * leaves that page to load on demand, as it would have anyway.
 */
export function prefetchPagesWhenIdle(): () => void {
  if (requestedCount === loaders.length || savingData()) return () => undefined;
  const idle = window as IdleWindow;
  let cancelled = false;
  let next = 0;
  let handle: number | undefined;
  const pump = () => {
    if (cancelled) return;
    const batch = loaders.slice(next, next + BATCH);
    next += BATCH;
    if (batch.length === 0) return;
    void Promise.allSettled(batch.map((fetchPage) => fetchPage())).then(
      schedule,
    );
  };
  const schedule = () => {
    if (cancelled) return;
    if (idle.requestIdleCallback) {
      handle = idle.requestIdleCallback(pump, { timeout: 2000 });
    } else {
      handle = window.setTimeout(pump, 200);
    }
  };
  schedule();
  return () => {
    cancelled = true;
    if (handle === undefined) return;
    if (idle.cancelIdleCallback) idle.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}

/**
 * Fetch every page chunk NOW — a pointer over, a focus on, or a finger down on
 * a link says a navigation is about to happen, and the idle pass may not have
 * reached that page yet.
 */
export function prefetchPagesNow(): void {
  if (requestedCount === loaders.length) return;
  requestedCount = loaders.length;
  loaders.forEach((fetchPage) => void fetchPage().catch(() => undefined));
}
